import React, { useMemo, useCallback } from "react";
import { Box, Typography, Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import GradientTypography from "../../components/patterns/typoraphy/GradientTypography";
import { useResponsive } from "../../hooks/useResponsive";

const LoginTopSection = ({ scrollProgress = 0, spacing = 2 }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isMobileOnly } = useResponsive();

  // Typography 설정 객체화 (메모이제이션)
  const typographyConfig = useMemo(
    () => ({
      variant: isMobileOnly ? "h5" : "h4", // 모바일에서 타이틀 사이즈 축소
      gradientThreads: {
        fontWeight: 800,
        display: "inline",
        whiteSpace: "pre",
      },
      gradientInsights: {
        fontWeight: 800,
        display: "inline",
        whiteSpace: "pre",
      },
      normalText: {
        fontWeight: 800,
        color: theme.palette.text.light,
        display: "inline",
        whiteSpace: "pre",
      },
      gradientProps: {
        angle: 90,
        animationSpeed: 0.75,
      },
    }),
    [theme.palette.text.light, isMobileOnly]
  );

  // 텍스트 분리 및 그라데이션 단어 적용 (메모이제이션)
  const renderMainMessage = useCallback(() => {
    const threads = t("landing.mainMessageWords.threads");
    const insights = t("landing.mainMessageWords.insights");

    // 전체 메시지에서 변수를 실제 값으로 치환하되, 그라데이션 단어들은 JSX로 변환
    const message = t("landing.mainMessage", {
      threads: `__GRADIENT_THREADS__`,
      insights: `__GRADIENT_INSIGHTS__`,
    });

    // 문자열을 분리하고 그라데이션 컴포넌트로 교체
    const parts = message.split(/(__GRADIENT_THREADS__|__GRADIENT_INSIGHTS__)/);

    return parts.map((part, index) => {
      if (part === "__GRADIENT_THREADS__") {
        return (
          <GradientTypography
            key={`threads-${index}`}
            text={threads}
            gradient={[theme.palette.primary.light, theme.palette.primary.main]}
            variant={typographyConfig.variant}
            angle={typographyConfig.gradientProps.angle}
            animationSpeed={typographyConfig.gradientProps.animationSpeed}
            sx={typographyConfig.gradientThreads}
          />
        );
      } else if (part === "__GRADIENT_INSIGHTS__") {
        return (
          <GradientTypography
            key={`insights-${index}`}
            text={insights}
            gradient={[theme.palette.primary.light, theme.palette.primary.main]}
            variant={typographyConfig.variant}
            angle={typographyConfig.gradientProps.angle}
            animationSpeed={typographyConfig.gradientProps.animationSpeed}
            sx={typographyConfig.gradientInsights}
          />
        );
      } else {
        return (
          <Typography
            key={`text-${index}`}
            variant={typographyConfig.variant}
            component="span"
            sx={typographyConfig.normalText}
          >
            {part}
          </Typography>
        );
      }
    });
  }, [
    t,
    theme.palette.primary.light,
    theme.palette.primary.main,
    typographyConfig,
  ]);

  // 로고 스타일 메모이제이션
  const logoStyle = useMemo(
    () => ({
      fontFamily: 'Neuton, serif',
      fontWeight: 700,
      color: theme.palette.text.primary,
      letterSpacing: '-0.01em',
    }),
    [theme.palette.text.primary]
  );

  // 텍스트 컨테이너 스타일 메모이제이션
  const textContainerStyle = useMemo(
    () => ({
      display: "block",
      textAlign: "center",
      minHeight: "4rem",
      whiteSpace: "pre-wrap",
      lineHeight: 1.2,
      maxWidth: "90vw",
      overflow: "hidden",
      wordBreak: "keep-all",
    }),
    []
  );

  return (
    <Stack
      direction="column"
      alignItems="center"
      spacing={spacing}
      sx={{
        width: "100%",
      }}
    >
      <Typography variant={isMobileOnly ? "h3" : "h2"} sx={logoStyle}>
        My Threads Insight
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={0}
        sx={{ width: "100%" }}
      >
        <Box sx={textContainerStyle}>{renderMainMessage()}</Box>
      </Stack>
    </Stack>
  );
};

export default LoginTopSection;
